
import { Component, Prop, Vue } from 'vue-property-decorator';
import { Website } from '@/services/Website/interfaces';
import { CertificateInterface, CertificatesRepositoryInterface } from '@/services/SSL/interfaces';
import ViewCertificateDialog from '@/components/dialogs/ssl/ViewCertificateDialog.vue';
import DeleteCertificateDialog from '@/components/dialogs/ssl/DeleteCertificateDialog.vue';
import CertificateLogsDialog from '@/components/dialogs/ssl/CertificateLogsDialog.vue';

@Component({
  inject: ['certificateRepository'],
  components: {
    ViewCertificateDialog,
    DeleteCertificateDialog,
    CertificateLogsDialog,
  },
})
export default class WebsiteSslPanel extends Vue {
  @Prop({ required: true }) public readonly website!: Website;

  @Prop({ default: null }) public readonly sslCertificateId!: number | null;

  public loading: boolean = true;

  public renewing: boolean = false;

  public certificate: CertificateInterface | null = null;

  public certificateDialog: string = '';

  public certificateForDialog: CertificateInterface | false = false;

  public deleteCertificateDialog: string = '';

  public certificateForDeleteDialog: CertificateInterface | false = false;

  public certificateLogsDialog: string = '';

  public certificateForLogsDialog: CertificateInterface | false = false;

  private readonly certificateRepository!: CertificatesRepositoryInterface;

  public get altNamesText(): string {
    if (!this.certificate || !this.certificate.altNames) {
      return '';
    }
    return this.certificate.altNames
      .map((item: any) => (typeof item === 'string' ? item : (item && item.name) || ''))
      .filter(Boolean)
      .join(', ');
  }

  public async created(): Promise<void> {
    await this.loadCertificate();
  }

  private async loadCertificate(): Promise<void> {
    this.loading = true;
    try {
      if (this.sslCertificateId === null || this.sslCertificateId === undefined) {
        this.certificate = null;
        return;
      }

      this.certificate = await this.certificateRepository.getCertificateById(this.sslCertificateId);
    } catch (err) {
      this.certificate = null;
      this.$errorDialog.show(
        err?.response?.data?.message || err?.response?.data?.error || err.message,
      );
    } finally {
      this.loading = false;
    }
  }

  public viewCertificate(): void {
    if (!this.certificate) {
      return;
    }

    this.certificateDialog = 'view-certificate-dialog';
    this.certificateForDialog = this.certificate;
  }

  public showLogsDialog(): void {
    if (!this.certificate) {
      return;
    }

    this.certificateLogsDialog = 'certificate-logs-dialog';
    this.certificateForLogsDialog = this.certificate;
  }

  public deleteCertificate(): void {
    if (!this.certificate) {
      return;
    }

    this.deleteCertificateDialog = 'delete-certificate-dialog';
    this.certificateForDeleteDialog = this.certificate;
  }

  public async renewCertificate(): Promise<void> {
    if (!this.certificate) {
      return;
    }

    this.renewing = true;
    try {
      await this.certificateRepository.renewCertificate(this.certificate.id);
      this.$successDialog.show('Certificate renewal started!');
      await this.loadCertificate();
    } catch (err) {
      this.$errorDialog.show(
        err?.response?.data?.message || err?.response?.data?.error || err.message,
      );
    } finally {
      this.renewing = false;
    }
  }

  public async recreate(): Promise<void> {
    if (!this.certificate) {
      return;
    }

    await this.$router.push({ name: 'certificate_create', query: { id: String(this.certificate.id) } });
  }

  public async newCertificate(): Promise<void> {
    await this.$router.push({ name: 'certificate_create' });
  }

  public onCertificateDeleted(): void {
    this.certificate = null;
    this.$emit('certificate-deleted');
  }
}
